package com.eladfinish.bible.contest.screens.browse.usecases

import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersState
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.model.ContentMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.CompleteType
import com.eladfinish.bible.contest.screens.chapterComplete.model.SavedCompletionData
import com.eladfinish.bible.contest.screens.chapterComplete.usecases.ChapterCompleteUseCaseProvider
import com.eladfinish.bible.contest.screens.chapterComplete.usecases.ParseSavedCompletionDataUseCase
import com.eladfinish.bible.contest.ui.models.ChapterTextAlign
import com.eladfinish.bible.contest.utils.MyStringUtils
import com.russhwolf.settings.Settings

class BuildPartReadyStateUseCase(
    private val settings: Settings,
    private val parseSavedCompletionDataUseCase: ParseSavedCompletionDataUseCase,
    private val chapterCompleteUseCaseProvider: ChapterCompleteUseCaseProvider,
) {

    data class Params(
        val isCompleteChapter: Boolean,
        val currentBook: Int,
        val partIndex: Int,
        val hebrewBookName: String,
        val chapterData: ChapterData,
        val completionDetails: String?,
    )

    operator fun invoke(
        params: Params,
    ): BrowseChaptersState.PartUiState.Ready {
        val shouldShowCompleteUi = params.isCompleteChapter

        val baseReady = BrowseChaptersState.PartUiState.Ready(
            chapterData = params.chapterData.copy(
                textAlign = if (shouldShowCompleteUi) {
                    ChapterTextAlign.START
                } else {
                    ChapterTextAlign.JUSTIFY
                },
            ),
            contentMode = ContentMode.Normal,
        )

        if (!shouldShowCompleteUi) return baseReady

        val saved = parseSavedCompletionDataUseCase(
            raw = params.completionDetails,
        )?.takeIf { data ->
            data.bookIndex == params.currentBook && data.chapter == params.partIndex
        } ?: return baseReady

        return buildSavedCompletionReady(
            params = params,
            savedCompletionData = saved,
        )
    }

    private fun buildSavedCompletionReady(
        params: Params,
        savedCompletionData: SavedCompletionData,
    ): BrowseChaptersState.PartUiState.Ready {
        val removeVerseLetter = settings.getBoolean(
            key = SharedPrefsConstants.KEY_REMOVE_VERSE_LETTER,
            defaultValue = false,
        )
        val eachVerseInNewLine = settings.getBoolean(
            key = SharedPrefsConstants.KEY_EACH_VERSE_IN_NEW_LINE,
            defaultValue = false,
        )

        val partitioned = chapterCompleteUseCaseProvider.partitionChapterUseCase(
            chapter = params.chapterData.content,
            isFromSifreyEmet = MyStringUtils.isFromSifreyEmet(
                bookNameHebrew = params.hebrewBookName,
            ),
        )

        val output = chapterCompleteUseCaseProvider.buildSavedCompletionOutputUseCase(
            partitionedChapter = partitioned,
            blanksPerVerse = savedCompletionData.blanks,
            removeVerseLetter = removeVerseLetter,
            eachVerseInNewLine = eachVerseInNewLine,
        )

        // Keep ContentMode.ChapterComplete so RevealBlankUseCase works without modifications.
        return BrowseChaptersState.PartUiState.Ready(
            chapterData = params.chapterData.copy(
                textAlign = ChapterTextAlign.START,
            ),
            contentMode = ContentMode.ChapterComplete(
                type = CompleteType.PARTIALLY_EXPOSURE,
                output = output,
            ),
        )
    }
}
